import { useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useToast } from '@/hooks/use-toast';

type PdfMode = 'exact-size' | 'fit-a4' | 'paginated-a4';

interface GeneratePdfOpts {
  filename?: string;
  mode?: PdfMode;
  scale?: number;
  background?: string;
  paddingPx?: number;
  marginMm?: number;
}

// Constantes A4
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const A4_WIDTH_PX = 794; // 210mm @ 96dpi

export const usePdfFromHtml = () => {
  const { toast } = useToast();

  const generatePdfFromElement = useCallback(async (
    elementId: string,
    opts: GeneratePdfOpts = {}
  ) => {
    try {
      const {
        filename = 'relatorio.pdf',
        mode = 'paginated-a4',
        background = '#ffffff',
        marginMm = 10,
      } = opts;

      const el = document.getElementById(elementId);
      if (!el) throw new Error(`Elemento #${elementId} não encontrado`);

      // ============================================
      // FASE 1: Preparar ambiente para captura
      // ============================================
      
      // Salvar estado original
      const htmlEl = document.documentElement;
      const wasDark = htmlEl.classList.contains('dark');
      const originalWidth = el.style.width;
      const originalMaxWidth = el.style.maxWidth;
      const originalMinWidth = el.style.minWidth;
      const originalPadding = el.style.padding;
      
      // Forçar tema claro
      if (wasDark) {
        htmlEl.classList.remove('dark');
        htmlEl.classList.add('light');
      }
      
      // Forçar largura A4 no container
      el.classList.add('pdf-capture');
      el.style.width = `${A4_WIDTH_PX}px`;
      el.style.maxWidth = `${A4_WIDTH_PX}px`;
      el.style.minWidth = `${A4_WIDTH_PX}px`;
      el.style.padding = '20px';

      // Garantir fontes carregadas
      try { await (document as any).fonts?.ready; } catch { /* noop */ }

      // Aguardar renderização completa com nova largura
      await new Promise(resolve => setTimeout(resolve, 400));

      toast({
        title: "Gerando PDF",
        description: "Capturando relatório em alta qualidade...",
      });

      // ============================================
      // FASE 2: Capturar canvas do HTML
      // ============================================
      
      // Escala para nitidez (2x é ideal para PDF)
      const scale = opts.scale ?? 2;

      // Dimensões do elemento após forçar largura A4
      const rect = el.getBoundingClientRect();
      const widthPx = Math.ceil(rect.width);
      const heightPx = Math.ceil(rect.height);

      const canvas = await html2canvas(el, {
        scale,
        backgroundColor: background,
        useCORS: true,
        allowTaint: false,
        logging: false,
        width: widthPx,
        height: heightPx,
        windowWidth: widthPx,
        windowHeight: heightPx,
      });

      // ============================================
      // FASE 3: Restaurar estado original
      // ============================================
      
      el.classList.remove('pdf-capture');
      el.style.width = originalWidth;
      el.style.maxWidth = originalMaxWidth;
      el.style.minWidth = originalMinWidth;
      el.style.padding = originalPadding;
      
      if (wasDark) {
        htmlEl.classList.remove('light');
        htmlEl.classList.add('dark');
      }

      // ============================================
      // FASE 4: Gerar PDF
      // ============================================
      
      const imgData = canvas.toDataURL('image/png');

      // Conversão: px -> mm (96 DPI)
      const pxToMm = (px: number) => (px * 25.4) / 96;

      const margin = marginMm;
      const usableWidth = A4_WIDTH_MM - margin * 2;
      const usableHeight = A4_HEIGHT_MM - margin * 2;

      if (mode === 'exact-size') {
        const pageWmm = pxToMm(widthPx);
        const pageHmm = pxToMm(heightPx);

        const pdf = new jsPDF({
          orientation: pageWmm > pageHmm ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [pageWmm, pageHmm],
          compress: true,
        });

        pdf.addImage(imgData, 'PNG', 0, 0, pageWmm, pageHmm, undefined, 'FAST');
        pdf.save(filename);

        toast({
          title: "PDF Gerado",
          description: "Download iniciado com sucesso!",
        });

        return true;
      }

      if (mode === 'fit-a4') {
        const imgWmm = pxToMm(widthPx);
        const imgHmm = pxToMm(heightPx);
        const scaleToFit = Math.min(usableWidth / imgWmm, usableHeight / imgHmm);

        const drawW = imgWmm * scaleToFit;
        const drawH = imgHmm * scaleToFit;
        const offsetX = (A4_WIDTH_MM - drawW) / 2;
        const offsetY = (A4_HEIGHT_MM - drawH) / 2;

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true,
        });

        pdf.addImage(imgData, 'PNG', offsetX, offsetY, drawW, drawH, undefined, 'FAST');
        pdf.save(filename);

        toast({
          title: "PDF Gerado",
          description: "Download iniciado com sucesso!",
        });

        return true;
      }

      // ============================================
      // MODE: paginated-a4 - PAGINAÇÃO AUTOMÁTICA
      // ============================================
      
      const imgWmm = pxToMm(widthPx);
      const imgHmm = pxToMm(heightPx);
      
      // Como forçamos 794px (A4), a escala será ~1:1
      // usableWidth = 190mm, imgWmm = ~210mm
      // scaleToFit = 190/210 = 0.9 (mínima redução para margem)
      const scaleToFit = usableWidth / imgWmm;
      const drawW = usableWidth;
      const drawH = imgHmm * scaleToFit;
      
      // Calcular número de páginas necessárias
      const totalPages = Math.ceil(drawH / usableHeight);
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        
        // Offset Y para cada página (negativo para "mover" imagem para cima)
        const yOffset = page * usableHeight;
        
        // Adicionar imagem com offset
        pdf.addImage(
          imgData, 
          'PNG', 
          margin,
          margin - yOffset,
          drawW, 
          drawH, 
          undefined, 
          'FAST'
        );
        
        // Máscara branca para ocultar overflow
        if (page < totalPages - 1) {
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, A4_HEIGHT_MM - margin, A4_WIDTH_MM, margin + 1, 'F');
        }
        if (page > 0) {
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, A4_WIDTH_MM, margin, 'F');
        }
        
        // Número da página
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`Página ${page + 1} de ${totalPages}`, A4_WIDTH_MM / 2, A4_HEIGHT_MM - 5, { align: 'center' });
      }

      pdf.save(filename);

      toast({
        title: "PDF Gerado",
        description: `Relatório com ${totalPages} página(s) gerado com sucesso!`,
      });

      return true;
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Erro ao Gerar PDF",
        description: error instanceof Error ? error.message : "Não foi possível gerar o PDF",
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  return { generatePdfFromElement };
};
